import { useEffect, useState } from "react";
import { Package, ArrowRight, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function ClientPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/packages");
      setPackages(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buy = async (pkg) => {
    setBuying(pkg.id);
    try {
      const { data: inv } = await api.post(`/client/packages/${pkg.id}/buy`);
      toast.success(`${pkg.name} reserved — redirecting to checkout…`);
      // Kick off Stripe checkout immediately
      const origin = window.location.origin;
      const { data: co } = await api.post("/payments/checkout", { invoice_id: inv.id, origin_url: origin });
      window.location.href = co.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBuying(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="client-packages">
      <div>
        <div className="tracker text-brand mb-3">[ Browse ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Packages.</h1>
        <p className="text-zinc-400 mt-3">Pre-built session bundles from your coach. Pay once, train multiple times.</p>
      </div>

      {loading ? (
        <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>
      ) : packages.length === 0 ? (
        <div className="card-flat p-16 text-center" data-testid="client-packages-empty">
          <Package size={32} weight="duotone" className="text-brand mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">No packages available yet.</div>
          <div className="text-zinc-500 tracker text-[10px]">Your coach hasn't published any bundles. Check back later.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="client-packages-grid">
          {packages.map((p) => (
            <div key={p.id} className="card-flat p-6 flex flex-col" data-testid={`client-package-card-${p.id}`}>
              <div className="tracker text-zinc-500 text-[10px] mb-2">{p.sessions_included} × {p.session_type}</div>
              <div className="font-display text-2xl leading-tight mb-2">{p.name}</div>
              {p.description && <p className="text-sm text-zinc-400 mb-4">{p.description}</p>}
              <ul className="space-y-2 mb-5 text-sm">
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> {p.sessions_included} dedicated 1:1 sessions</li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> Per-session value <span className="money font-mono">{(p.currency || "usd").toUpperCase()} {(Number(p.price) / Number(p.sessions_included)).toFixed(2)}</span></li>
                <li className="flex items-start gap-2"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> Sessions credited to your pot on payment</li>
              </ul>
              <div className="font-display text-4xl money mt-auto mb-4">{(p.currency || "usd").toUpperCase()} {Number(p.price).toFixed(2)}</div>
              <button
                onClick={() => buy(p)}
                disabled={buying === p.id}
                className="btn-primary justify-center"
                data-testid={`buy-package-${p.id}`}
              >
                {buying === p.id ? "Redirecting…" : <>Buy now <ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
